import { createGlobalStyle } from 'styled-components';
import { theme } from './theme';

const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
    /* iOS Safari: prevent address bar from affecting layout */
    min-height: 100vh;
    min-height: 100dvh;
  }

  body {
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: ${theme.typography.lineHeight.normal};
    color: ${theme.colors.text.primary};
    background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
    min-height: 100vh;
    min-height: 100dvh;
    overflow-x: hidden;
    /* iPhone notch & home indicator safe areas */
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }

  /* Prevent white flash on page load */
  html, body, #root {
    background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  }

  /* iOS: ensure #root fills viewport (iPhone 13 Pro Max 428×926) */
  #root {
    min-height: 100vh;
    min-height: 100dvh;
  }

  /* Ensure all pages have the correct background immediately */
  .min-h-screen {
    background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%) !important;
  }

  /* Specific fix for Vessels page to prevent white flash */
  [style*="backgroundColor: '#0f172a'"] {
    background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%) !important;
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${theme.colors.background.secondary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary.main};
    border-radius: ${theme.borderRadius.full};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.primary.light};
  }

  /* Selection styling */
  ::selection {
    background: ${theme.colors.primary.main};
    color: ${theme.colors.primary.contrast};
  }

  /* Media & layout safety to prevent horizontal overflow */
  img, video, canvas, svg {
    max-width: 100%;
    height: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    display: block;
    overflow-x: auto;
  }

  pre, code {
    white-space: pre-wrap;
    word-break: break-word;
  }

  main, header, footer, section, nav, .card {
    max-width: 100vw;
    max-width: min(100vw, 100%);
  }

  /* Prevent horizontal overflow on mobile - iPhone 13 Pro Max 428px */
  @media (max-width: 428px) {
    * {
      -webkit-tap-highlight-color: transparent;
    }
    input, textarea, select {
      font-size: 16px; /* Prevents iOS zoom on focus */
    }
  }

  /* Focus styling */
  :focus {
    outline: 2px solid ${theme.colors.primary.main};
    outline-offset: 2px;
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.typography.fontFamily.secondary};
    font-weight: ${theme.typography.fontWeight.semibold};
    line-height: ${theme.typography.lineHeight.tight};
    color: ${theme.colors.text.primary};
    margin-bottom: ${theme.spacing.md};
  }

  h1 {
    font-size: ${theme.typography.fontSize['5xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    background: linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.secondary.main} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  h2 {
    font-size: ${theme.typography.fontSize['4xl']};
    font-weight: ${theme.typography.fontWeight.bold};
  }

  h3 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.semibold};
  }

  h4 {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.semibold};
  }

  h5 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.medium};
  }

  h6 {
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.medium};
  }

  p {
    margin-bottom: ${theme.spacing.md};
    color: ${theme.colors.text.secondary};
    line-height: ${theme.typography.lineHeight.relaxed};
  }

  a {
    color: ${theme.colors.primary.light};
    text-decoration: none;
    transition: ${theme.transitions.colors};
    
    &:hover {
      color: ${theme.colors.primary.main};
    }
  }

  /* Button base styles */
  button {
    font-family: inherit;
    font-size: inherit;
    border: none;
    cursor: pointer;
    transition: ${theme.transitions.default};
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  /* Input base styles */
  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    border: 1px solid ${theme.colors.border.primary};
    border-radius: ${theme.borderRadius.md};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    background: ${theme.colors.background.paper};
    color: ${theme.colors.text.primary};
    transition: ${theme.transitions.colors};
    
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary.main};
      box-shadow: 0 0 0 3px ${theme.colors.primary.main}20;
    }
    
    &::placeholder {
      color: ${theme.colors.text.disabled};
    }
  }

  /* Card base styles */
  .card {
    background: ${theme.colors.background.paper};
    border-radius: ${theme.borderRadius.xl};
    padding: ${theme.spacing.lg};
    box-shadow: ${theme.shadows.md};
    border: 1px solid ${theme.colors.border.primary};
    transition: ${theme.transitions.default};
    
    &:hover {
      box-shadow: ${theme.shadows.lg};
      transform: translateY(-2px);
    }
  }

  /* Utility classes */
  .text-center { text-align: center; }
  .text-left { text-align: left; }
  .text-right { text-align: right; }
  
  .text-primary { color: ${theme.colors.primary.main}; }
  .text-secondary { color: ${theme.colors.secondary.main}; }
  .text-accent { color: ${theme.colors.accent.main}; }
  .text-success { color: ${theme.colors.success.main}; }
  .text-warning { color: ${theme.colors.warning.main}; }
  .text-error { color: ${theme.colors.error.main}; }
  
  .bg-primary { background-color: ${theme.colors.primary.main}; }
  .bg-secondary { background-color: ${theme.colors.secondary.main}; }
  .bg-accent { background-color: ${theme.colors.accent.main}; }
  .bg-success { background-color: ${theme.colors.success.main}; }
  .bg-warning { background-color: ${theme.colors.warning.main}; }
  .bg-error { background-color: ${theme.colors.error.main}; }
  
  .rounded { border-radius: ${theme.borderRadius.md}; }
  .rounded-lg { border-radius: ${theme.borderRadius.lg}; }
  .rounded-xl { border-radius: ${theme.borderRadius.xl}; }
  .rounded-full { border-radius: ${theme.borderRadius.full}; }
  
  .shadow { box-shadow: ${theme.shadows.base}; }
  .shadow-md { box-shadow: ${theme.shadows.md}; }
  .shadow-lg { box-shadow: ${theme.shadows.lg}; }
  .shadow-xl { box-shadow: ${theme.shadows.xl}; }

  /* Animation classes */
  .fade-in {
    animation: fadeIn 0.5s ease-in-out;
  }

  .slide-up {
    animation: slideUp 0.3s ease-out;
  }

  .scale-in {
    animation: scaleIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @keyframes scaleIn {
    from { transform: scale(0.95); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }

  /* Responsive utilities - iPhone 13 Pro Max (428px) and larger phones */
  @media (max-width: ${theme.breakpoints.md}) {
    html {
      font-size: 14px;
    }
    
    h1 { font-size: ${theme.typography.fontSize['4xl']}; }
    h2 { font-size: ${theme.typography.fontSize['3xl']}; }
    h3 { font-size: ${theme.typography.fontSize['2xl']}; }
  }

  /* iPhone 13 Pro Max viewport: 428px - keep readable base size */
  @media (max-width: 428px) {
    html {
      font-size: 14px;
    }
    
    h1 { font-size: ${theme.typography.fontSize['3xl']}; }
    h2 { font-size: ${theme.typography.fontSize['2xl']}; }
    h3 { font-size: ${theme.typography.fontSize.xl}; }
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    html {
      font-size: 13px;
    }
    
    h1 { font-size: ${theme.typography.fontSize['3xl']}; }
    h2 { font-size: ${theme.typography.fontSize['2xl']}; }
    h3 { font-size: ${theme.typography.fontSize.xl}; }
  }

  /* Small phones (< 375px) */
  @media (max-width: 374px) {
    html {
      font-size: 12px;
    }
    
    h1 { font-size: ${theme.typography.fontSize['2xl']}; }
    h2 { font-size: ${theme.typography.fontSize.xl}; }
    h3 { font-size: ${theme.typography.fontSize.lg}; }
  }

  /* Print styles */
  @media print {
    body {
      background: white;
      color: black;
    }
    
    .no-print {
      display: none !important;
    }
  }
`;

export default GlobalStyles;
